import {
  Card,
  CardHeader,
  makeStyles,
  tokens,
  Text,
  Body2,
  Caption1,
  Badge,
  Divider,
} from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import type { DiagnosticGap } from '../types';
import { getSeverityBadgeColor } from '../utils/scoring';

interface TopGapsListProps {
  gaps: DiagnosticGap[];
}

const useStyles = makeStyles({
  gapItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
  },
  gapHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalXXS,
  },
  emptyState: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
  },
});

export function TopGapsList({ gaps }: TopGapsListProps) {
  const styles = useStyles();

  return (
    <Card>
      <CardHeader
        image={<WarningRegular style={{ fontSize: '20px', color: '#C4A000' }} />}
        header={<Text weight="semibold">Top Governance Gaps</Text>}
        description={<Body2>{gaps.length} gap{gaps.length !== 1 ? 's' : ''} identified</Body2>}
      />
      {gaps.length === 0 ? (
        <div className={styles.emptyState}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            No governance gaps detected. Your security posture is strong.
          </Caption1>
        </div>
      ) : (
        gaps.map((gap, index) => (
          <div key={index}>
            {index > 0 && <Divider />}
            <div className={styles.gapItem}>
              <div className={styles.gapHeader}>
                <Text size={300} weight="semibold">{gap.description}</Text>
              </div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {gap.recommendedAction}
              </Caption1>
              <div className={styles.badges}>
                <Badge appearance="tint" color={getSeverityBadgeColor(gap.severity)} size="medium">
                  {gap.severity}
                </Badge>
                <Badge appearance="outline" size="medium">
                  {gap.pillar}
                </Badge>
                {gap.estimatedEffort && (
                  <Badge appearance="outline" color="informative" size="medium">
                    Effort: {gap.estimatedEffort}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </Card>
  );
}
